/**
 * One view on the screen: a camera following its subject (the local player or another
 * racer) through the subject's level, with that racer drawn solid, everybody else on the
 * same level as ghosts, and the view's own trails, particles and effects. On a boss level,
 * the boss chasing the subject behind the level and its attacks over it (in the subject's
 * moment of the level, as the level's moving things).
 *
 * The controller feeds every view the same snapshot of all racers each frame; a view picks
 * its subject and the ghosts that share its level, keeps the camera smooth, and snaps when
 * the subject's run jumps (new attempt, next level, another subject).
 */
import {
  BOOSTS,
  type BoostType,
  boostBit,
  MODE_BOXES,
  type Mode,
  type PlayerId,
  type SimEvent,
} from '@cube-rush/shared';
import { Backdrop } from '../render/background';
import {
  type AttackDrawState,
  bossMiddle,
  bossMuzzle,
  drawAttacks,
  drawBoss,
} from '../render/boss';
import { drawFlash, drawSlowmo, drawSpeedLines } from '../render/effects';
import { type LevelDrawState, ORB_COLORS, PAD_COLORS, PORTAL_COLORS } from '../render/levelArt';
import { Particles } from '../render/particles';
import {
  drawRacer,
  drawRacerTag,
  type RacerArt,
  type RacerDrawOptions,
  type RacerFrame,
  TagLayout,
} from '../render/racer';
import { SeasonDecor } from '../render/seasonDecor';
import { Trail, WaveTrail } from '../render/trails';
import { ViewTransform } from '../render/view';
import type { Season } from '../season/season';
import { BossChase, type BossEvents } from './boss';
import { Camera, type CameraTarget } from './camera';
import { TELEPORT_DY } from './ghosts';
import type { Rect } from './layout';
import type { LevelBundle, LevelResources } from './levelResources';

export type ViewSubject = { kind: 'local' } | { kind: 'remote'; id: PlayerId };

export function sameSubject(a: ViewSubject, b: ViewSubject): boolean {
  if (a.kind === 'local' || b.kind === 'local') return a.kind === b.kind;
  return a.id === b.id;
}

/** A racer as the views see it in one frame. */
export interface RacerSnapshot extends RacerFrame {
  id: PlayerId;
  level: number;
  attempt: number;
  /** No news from this racer for a while (offline, finished, gave up). */
  stale: boolean;
  art: RacerArt;
}

/** Everything a view needs for one frame besides its subject. */
export interface ViewFrame {
  /** Animation clock, seconds. */
  time: number;
  /** Real seconds since the previous frame (capped). */
  dt: number;
  /** Device pixels per CSS pixel of the canvas. */
  scale: number;
  showGhosts: boolean;
  reducedMotion: boolean;
  /** Every racer known this frame, the local one included. */
  racers: readonly RacerSnapshot[];
  /** Holiday decorations (GameSettings.season). */
  season: Season;
  /** Adaptive resolution multiplier, 0.5..1: decorations thin out on a struggling device. */
  quality: number;
}

/** Extra state of the local player's view. */
export interface LocalViewState {
  collected: ReadonlySet<number>;
  orbFocus: { x: number; y: number; half: number } | null;
}

const GHOST_ALPHA = 0.42;
const GHOST: RacerDrawOptions = { alpha: GHOST_ALPHA, glow: false };
const SOLID: RacerDrawOptions = { alpha: 1, glow: true };
/** A subject without news for a while (offline, gave up) fades. */
const FADED: RacerDrawOptions = { alpha: 0.5, glow: true };
/** How often a saw blade cutting into a surface throws its sparks (bursts per second). */
const SPARKS_PER_SECOND = 14;
/** The boss's crash smoke comes up at least this far (blocks) inside the view's left edge. */
const CRASH_INSET = 2;
const TURBO = boostBit('turbo');
const SLOWMO = boostBit('slowmo');

export class GameView {
  rect: Rect = { x: 0, y: 0, w: 1, h: 1 };
  readonly camera = new Camera();
  readonly transform = new ViewTransform();
  readonly particles = new Particles();
  private readonly trail = new Trail();
  private readonly waveTrail = new WaveTrail();
  private readonly backdrop = new Backdrop();
  private readonly decor = new SeasonDecor();
  private bundle: LevelBundle | null = null;
  private lastAttempt = -1;
  private lastAlive = true;
  private needsSnap = true;
  private flashAmount = 0;
  private flashColor = '#ffffff';
  private turbo = 0;
  private slowmo = 0;
  private readonly ghostAlive = new Map<PlayerId, boolean>();
  /** Where each ghost was drawn last frame (a spider's teleport shows as a streak). */
  private readonly ghostLast = new Map<PlayerId, Seen>();
  /** The subject followed from afar as drawn last frame (its teleports, see update). */
  private readonly remoteLast: Seen = { mode: 'cube', gravity: 1, y: 0 };
  private remoteKnown = false;
  /** The subject as drawn this frame (null: nobody, the level start is shown). */
  private current: RacerSnapshot | null = null;
  private local: LocalViewState | null = null;
  private readonly startTarget = { x: 0, y: 0, gravity: 1 as 1 | -1, alive: true };
  private readonly tags = new TagLayout();
  /** Scratch: where saw blades in view throw sparks (see LevelArt.sparks). */
  private readonly sparkSpots: number[] = [];
  /** A boss level's boss, chasing the subject (see game/boss.ts), and scratch for its points. */
  private readonly boss = new BossChase();
  private bossShown = false;
  private readonly point = { x: 0, y: 0 };
  private readonly attackState: AttackDrawState = { tick: 0, time: 0, muzzleX: null, run: null };
  private readonly levelState: LevelDrawState = {
    collected: null,
    hidePickupsBefore: Number.NEGATIVE_INFINITY,
    orbFocus: null,
    tick: 0,
  };

  constructor(
    public subject: ViewSubject,
    private readonly levels: LevelResources,
  ) {}

  /** Level index the view shows. */
  get levelIndex(): number {
    return this.bundle?.index ?? 0;
  }

  /** The subject as drawn in the last frame. */
  get shown(): RacerSnapshot | null {
    return this.current;
  }

  /** Racers drawn as ghosts in this view in the last frame (debug status: allocates). */
  ghostIds(frame: ViewFrame): PlayerId[] {
    const bundle = this.bundle;
    if (!frame.showGhosts || !bundle) return [];
    const subject = this.current;
    return frame.racers
      .filter((racer) => isGhost(racer, subject, bundle.index))
      .map((racer) => racer.id);
  }

  /** The view is gone: shared drawing resources go back (the backdrop layers). */
  dispose(): void {
    this.backdrop.dispose();
  }

  setSubject(subject: ViewSubject): void {
    if (sameSubject(subject, this.subject)) return;
    this.subject = subject;
    this.needsSnap = true;
    this.trail.clear();
    this.waveTrail.clear();
    this.particles.clear();
  }

  /**
   * Places the view; `insetTop` / `insetBottom` = CSS px at its top / bottom covered by the
   * HUD (the top bar; boost buttons or the spectator bar).
   */
  setRect(rect: Rect, insetTop = 0, insetBottom = 0): void {
    const r = this.rect;
    // A different size changes the zoom: jump to the new framing rather than animate.
    if (r.w !== rect.w || r.h !== rect.h || this.camera.insetTop !== insetTop) {
      this.needsSnap = true;
    }
    this.rect = rect;
    this.camera.insetTop = insetTop;
    // Controls coming or going (the boost buttons at the finish) move the camera smoothly.
    this.camera.insetBottom = insetBottom;
  }

  /**
   * Moves the camera for this frame. `subject` is the racer to follow (null when nothing is
   * known yet: the view shows the start of `fallbackLevel`).
   */
  update(
    subject: RacerSnapshot | null,
    fallbackLevel: number,
    frame: ViewFrame,
    local: LocalViewState | null,
  ): void {
    this.current = subject;
    this.local = local;
    const bundle = this.levels.get(subject?.level ?? fallbackLevel);
    if (bundle !== this.bundle) {
      this.bundle = bundle;
      this.needsSnap = true;
      this.trail.clear();
      this.waveTrail.clear();
      this.ghostAlive.clear();
      this.ghostLast.clear();
      this.remoteKnown = false;
    }
    if (!subject) {
      // Nobody to follow yet: the camera looks at the level start.
      const start = bundle.level.start;
      this.startTarget.x = start.x;
      this.startTarget.y = start.y;
      this.startTarget.gravity = start.gravity;
    }
    const target: CameraTarget & { alive: boolean } = subject ?? this.startTarget;
    if (subject) {
      if (subject.attempt !== this.lastAttempt) {
        this.lastAttempt = subject.attempt;
        this.needsSnap = true;
        this.trail.clear();
        this.waveTrail.clear();
      }
      if (this.lastAlive && !subject.alive && this.subject.kind === 'remote') {
        // Another racer crashed: their explosion (the local one comes with its event).
        this.particles.explosion(subject.x, subject.y, subject.art.primary, subject.art.secondary);
      }
      this.lastAlive = subject.alive;
      if (this.subject.kind === 'remote') this.watchRemote(subject);
    }

    // The boss of a boss level chases the subject: it comes in roaring, its attacks shake the view.
    this.bossShown = false;
    if (subject && bundle.level.boss !== undefined) {
      const events = this.boss.update(
        bundle.level,
        bundle.world.attacks,
        `${bundle.index}:${subject.attempt}`,
        subject.x,
        bundle.guide.tickAt(subject.x),
      );
      if (events) {
        this.bossShown = true;
        this.onBoss(events);
      }
    }

    const { w, h } = this.rect;
    if (this.needsSnap) {
      this.camera.snap(target, bundle.guide, w, h);
      this.needsSnap = false;
    } else if (target.alive) {
      this.camera.follow(target, bundle.guide, w, h, frame.dt);
    } else {
      this.camera.hold(w, h, frame.dt);
    }
    const shakeX = frame.reducedMotion ? 0 : this.camera.shakeOffset(frame.time, 0);
    const shakeY = frame.reducedMotion ? 0 : this.camera.shakeOffset(frame.time, 1);
    this.transform.set(
      this.rect,
      this.camera.left + shakeX,
      this.camera.bottom + shakeY,
      this.camera.ppb,
    );

    if (subject?.alive) {
      if (subject.mode === 'wave') {
        this.waveTrail.add(subject.x, subject.y);
        this.trail.clear();
      } else {
        // The zigzag of an earlier wave stretch stays where it is and scrolls away.
        this.trail.add(subject.x, subject.y);
      }
    }
    this.watchGhosts(frame);
    // Sparks where saw blades cut into a surface, a few a second each (none with reduced motion).
    if (!frame.reducedMotion && bundle.art.hasSparks) {
      const tick = subject ? bundle.guide.tickAt(subject.x) : 0;
      const spots = this.sparkSpots;
      const n = bundle.art.sparks(this.transform, tick, spots);
      for (let k = 0; k < n; k++) {
        if (Math.random() < frame.dt * SPARKS_PER_SECOND) {
          this.particles.sawSparks(
            spots[3 * k]!,
            spots[3 * k + 1]!,
            spots[3 * k + 2]! > 0 ? 1 : -1,
          );
        }
      }
    }
    this.particles.step(frame.dt);
    const fx = subject?.fx ?? 0;
    this.turbo = approach(this.turbo, fx & TURBO ? 1 : 0, frame.dt * 4);
    this.slowmo = approach(this.slowmo, fx & SLOWMO ? 1 : 0, frame.dt * 3);
    this.flashAmount = Math.max(0, this.flashAmount - frame.dt * 3.5);
  }

  draw(ctx: CanvasRenderingContext2D, frame: ViewFrame): void {
    const bundle = this.bundle;
    if (!bundle) return;
    const view = this.transform;
    const { scale, time } = frame;
    ctx.save();
    ctx.beginPath();
    ctx.rect(view.x, view.y, view.w, view.h);
    ctx.clip();

    this.backdrop.draw(ctx, bundle.art.theme, view, scale, time);
    this.decor.drawBehind(ctx, view, frame);
    const subject = this.current;
    const local = this.local;
    const levelState = this.levelState;
    levelState.collected = local?.collected ?? null;
    // Racers watched from afar collect pickups unseen: hide the ones they passed.
    levelState.hidePickupsBefore = local || !subject ? Number.NEGATIVE_INFINITY : subject.x - 0.5;
    levelState.orbFocus = local?.orbFocus ?? null;
    // Its racer's moment of the level: what moves is where that racer meets it.
    levelState.tick = subject ? bundle.guide.tickAt(subject.x) : 0;
    // A boss level: the boss behind the level, its attacks over it (under the racers).
    if (this.bossShown) drawBoss(ctx, view, scale, this.boss.pose, time);
    bundle.art.draw(ctx, view, scale, time, levelState);
    this.decor.drawOnLevel(ctx, view, frame, bundle.level);
    if (bundle.world.attacks.length > 0) {
      const attacks = this.attackState;
      attacks.tick = levelState.tick;
      attacks.time = time;
      attacks.muzzleX =
        this.bossShown && this.boss.pose.kind === 'robot'
          ? bossMuzzle(this.boss.pose, this.point).x
          : null;
      attacks.run = subject ? bundle.guide : null;
      drawAttacks(ctx, view, bundle.level, bundle.world, attacks);
    }

    if (frame.showGhosts) {
      // The shadows, then their name tags over them (moved apart where racers bunch up).
      for (const racer of frame.racers) {
        if (isGhost(racer, subject, bundle.index)) {
          drawRacer(ctx, view, scale, racer.art, racer, time, GHOST);
        }
      }
      this.tags.clear();
      for (const racer of frame.racers) {
        if (isGhost(racer, subject, bundle.index)) {
          drawRacerTag(ctx, view, scale, racer.art, racer, GHOST_ALPHA, this.tags);
        }
      }
    }

    if (subject) {
      const size = view.len(MODE_BOXES[subject.mode].outer.h);
      if (this.waveTrail.size > 1) this.waveTrail.draw(ctx, view, subject.art.accent);
      if (subject.alive && subject.mode !== 'wave') {
        this.trail.draw(
          ctx,
          view,
          scale,
          subject.art.profile.skin.trail,
          subject.art.trailColors,
          size,
          time,
          subject.x,
          subject.y,
        );
      }
      drawRacer(ctx, view, scale, subject.art, subject, time, subject.stale ? FADED : SOLID);
    }
    this.particles.draw(ctx, view);
    drawSpeedLines(ctx, view, time, this.turbo);
    drawSlowmo(ctx, view, this.slowmo);
    drawFlash(ctx, view, this.flashColor, this.flashAmount);
    ctx.restore();
  }

  /**
   * Visual reaction of the local player's view to a simulation event; `teleport`: where a
   * spider's teleport took it from and to.
   */
  localEvent(
    event: SimEvent,
    art: RacerArt,
    x: number,
    y: number,
    gravity: 1 | -1,
    teleport?: { from: number; to: number },
  ): void {
    const level = this.bundle?.level;
    switch (event.type) {
      case 'jump':
        if (teleport) this.teleport(x, teleport.from, teleport.to, art.accent, 1);
        break;
      case 'death':
        this.particles.explosion(event.x, event.y, art.primary, art.secondary);
        this.camera.shake(0.22);
        this.flash('#ffffff', 0.7);
        break;
      case 'land':
        this.particles.dust(x, y - gravity * 0.5, gravity, art.accent);
        break;
      case 'orb': {
        const o = level?.objects[event.object];
        if (o) this.particles.burst(o.x + o.w / 2, o.y + o.h / 2, ORB_COLORS[event.color], 1);
        break;
      }
      case 'pad': {
        const o = level?.objects[event.object];
        if (o) this.particles.burst(o.x + o.w / 2, o.y + o.h, PAD_COLORS[event.color], gravity);
        break;
      }
      case 'portal':
        this.particles.ring(x, y, PORTAL_COLORS[event.portal], 1.6, 0.4);
        break;
      case 'pickup': {
        const o = level?.objects[event.object];
        if (o && !event.ignored)
          this.particles.sparkle(o.x + o.w / 2, o.y + o.h / 2, boostColor(event.boost));
        break;
      }
      case 'boostStart':
        this.particles.ring(x, y, boostColor(event.boost), 2, 0.5);
        this.flash(boostColor(event.boost), 0.35);
        break;
      case 'levelComplete': {
        const view = this.transform;
        this.particles.confetti(view.left, view.right, view.top);
        this.flash(this.bundle?.art.theme.accent ?? '#ffffff', 0.5);
        break;
      }
      case 'respawn':
      case 'levelStart':
        this.needsSnap = true;
        this.trail.clear();
        this.waveTrail.clear();
        break;
      default:
        break;
    }
  }

  private flash(color: string, amount: number): void {
    this.flashColor = color;
    this.flashAmount = amount;
  }

  /**
   * What the boss did this frame: it came in (a shake, as it roars), an attack went off (a
   * geyser's or a falling fire's burst of embers, a jolt for the big ones), it crashed behind the
   * subject (smoke, stars and a jolt: the escape).
   */
  private onBoss(events: BossEvents): void {
    if (events.entered) this.camera.shake(0.12);
    for (const a of events.fired) {
      if (a.attack === 'flame') {
        const x = a.x + a.w / 2;
        const ground = a.grows !== 'down';
        this.particles.burst(x, ground ? 0.2 : a.y, '#ff8a1f', ground ? 1 : -1);
        if (ground && a.h > 0.9) this.camera.shake(0.06);
      } else if (a.attack === 'laser') {
        this.camera.shake(0.04);
      }
    }
    if (events.crashed) {
      const at = bossMiddle(this.boss.pose, this.point);
      // Its chest may be at the view's very edge: the smoke and stars come up inside the view.
      const x = Math.max(at.x, this.transform.left + CRASH_INSET);
      const dark = this.boss.pose.kind === 'dragon' ? '#6e1a1a' : '#2e3a4c';
      this.particles.explosion(x, at.y, '#9a9a9a', dark);
      this.particles.sparkle(x + 1, at.y + 1.5, '#ffd60a');
      this.camera.shake(0.2);
    }
  }

  /**
   * A spider's teleport: a streak from where it left to where it came (`strength` 1 for the
   * view's subject, less for a ghost); the subject's trail starts anew over there.
   */
  private teleport(x: number, from: number, to: number, color: string, strength: number): void {
    this.particles.teleport(x, from, to, color, strength);
    if (strength >= 1) this.trail.clear();
  }

  /** The subject followed from afar: its teleports between two frames (no event tells them). */
  private watchRemote(subject: RacerSnapshot): void {
    const last = this.remoteLast;
    if (this.remoteKnown && subject.alive && teleportedSince(last, subject)) {
      this.teleport(subject.x, last.y, subject.y, subject.art.accent, 1);
    }
    see(last, subject);
    this.remoteKnown = true;
  }

  /** Ghost crashes get a small explosion too, their teleports a faint streak. */
  private watchGhosts(frame: ViewFrame): void {
    const index = this.bundle?.index;
    for (const racer of frame.racers) {
      if (racer.level !== index || racer === this.current) continue;
      const was = this.ghostAlive.get(racer.id);
      const shown = frame.showGhosts && !racer.stale;
      if (was === true && !racer.alive && shown) {
        this.particles.ring(racer.x, racer.y, racer.art.accent, 1.2, 0.35);
      }
      this.ghostAlive.set(racer.id, racer.alive);
      const last = this.ghostLast.get(racer.id);
      if (!last) {
        this.ghostLast.set(racer.id, see({ mode: 'cube', gravity: 1, y: 0 }, racer));
        continue;
      }
      if (was === true && racer.alive && shown && teleportedSince(last, racer)) {
        this.teleport(racer.x, last.y, racer.y, racer.art.accent, 0.5);
      }
      see(last, racer);
    }
  }
}

/** A racer as a view saw it last frame. */
interface Seen {
  mode: Mode;
  gravity: 1 | -1;
  y: number;
}

/** Remembers how a racer was seen this frame (in place). */
function see(seen: Seen, racer: RacerFrame): Seen {
  seen.mode = racer.mode;
  seen.gravity = racer.gravity;
  seen.y = racer.y;
  return seen;
}

/** A spider teleported since it was seen (see TELEPORT_DY): its height jumped, gravity flipped. */
function teleportedSince(seen: Seen, racer: RacerFrame): boolean {
  return (
    seen.mode === 'spider' &&
    racer.mode === 'spider' &&
    seen.gravity !== racer.gravity &&
    Math.abs(racer.y - seen.y) >= TELEPORT_DY
  );
}

/** Is `racer` drawn as a shadow in a view of `level` that follows `subject`? */
function isGhost(racer: RacerSnapshot, subject: RacerSnapshot | null, level: number): boolean {
  return (
    racer !== subject &&
    racer.level === level &&
    !racer.stale &&
    (subject === null || racer.id !== subject.id)
  );
}

function boostColor(boost: BoostType): string {
  return BOOSTS[boost]?.color ?? '#ffffff';
}

function approach(value: number, target: number, step: number): number {
  return value < target ? Math.min(target, value + step) : Math.max(target, value - step);
}
